import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Tag, Space, Button, Skeleton, message,
  Table, Tabs, Badge, Form, Input, InputNumber, Select, Modal, Divider, Statistic,
} from 'antd';
import {
  AppstoreOutlined, HomeOutlined, ArrowLeftOutlined,
  SwapOutlined, ArrowUpOutlined, ArrowDownOutlined, AimOutlined,
  EditOutlined, HistoryOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';
import Footer from '../components/Footer';

const { Option } = Select;

const CATEGORIAS = [
  { valor: 'cables',       label: 'Cables',       color: 'blue'    },
  { valor: 'arneses',      label: 'Arneses',       color: 'cyan'    },
  { valor: 'antenas',      label: 'Antenas',       color: 'green'   },
  { valor: 'tornilleria',  label: 'Tornillería',   color: 'default' },
  { valor: 'herramientas', label: 'Herramientas',  color: 'orange'  },
  { valor: 'consumibles',  label: 'Consumibles',   color: 'purple'  },
  { valor: 'electronica',  label: 'Electrónica',   color: 'red'     },
  { valor: 'otros',        label: 'Otros',         color: 'default' },
];
const catColor = (c) => CATEGORIAS.find(x => x.valor === c)?.color || 'default';
const catLabel = (c) => CATEGORIAS.find(x => x.valor === c)?.label || c;

const MOV_CONFIG = {
  entrada: { label: 'Entrada',  color: 'success', icon: <ArrowUpOutlined /> },
  salida:  { label: 'Salida',   color: 'error',   icon: <ArrowDownOutlined /> },
  ajuste:  { label: 'Ajuste',   color: 'warning', icon: <AimOutlined /> },
};

const MOTIVOS = {
  entrada: ['Compra', 'Devolución de técnico', 'Transferencia entre depósitos', 'Donación', 'Otro'],
  salida:  ['Orden de trabajo', 'Pérdida / extravío', 'Uso interno', 'Devolución a proveedor', 'Baja por daño', 'Otro'],
  ajuste:  ['Conteo físico', 'Corrección de error de carga', 'Otro'],
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-PY', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const AccesorioDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem]       = useState(null);
  const [movs, setMovs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [movLoading, setMovLoading] = useState(false);

  // Modal movimiento
  const [movModalOpen, setMovModalOpen] = useState(false);
  const [movForm]                       = Form.useForm();
  const movTipo = Form.useWatch('tipo', movForm);

  // Modal editar
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm]                        = Form.useForm();

  const cargarItem = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/accesorios/${id}`);
      setItem(r.data);
    } catch {
      message.error('No se pudo cargar el ítem');
    }
  }, [id]);

  const cargarMovimientos = useCallback(async () => {
    setMovLoading(true);
    try {
      const r = await axiosInstance.get(`/accesorios/${id}/movimientos`);
      setMovs(r.data);
    } catch {
      message.error('Error al cargar movimientos');
    } finally {
      setMovLoading(false);
    }
  }, [id]);

  const cargar = useCallback(async () => {
    setLoading(true);
    await Promise.all([cargarItem(), cargarMovimientos()]);
    setLoading(false);
  }, [cargarItem, cargarMovimientos]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Registrar movimiento ─────────────────────────────────────────────────────
  const abrirMovimiento = () => {
    movForm.resetFields();
    movForm.setFieldsValue({ tipo: 'salida', cantidad: 1 });
    setMovModalOpen(true);
  };

  const guardarMovimiento = async () => {
    try {
      const values = await movForm.validateFields();
      await axiosInstance.post(`/accesorios/${id}/movimiento`, values);
      message.success('Movimiento registrado');
      setMovModalOpen(false);
      await cargar();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error || 'Error al registrar movimiento');
    }
  };

  // ── Editar metadatos ─────────────────────────────────────────────────────────
  const abrirEditar = () => {
    editForm.setFieldsValue(item);
    setEditModalOpen(true);
  };

  const guardarEditar = async () => {
    try {
      const values = await editForm.validateFields();
      await axiosInstance.put(`/accesorios/${id}`, values);
      message.success('Ítem actualizado');
      setEditModalOpen(false);
      cargarItem();
    } catch (e) {
      if (e?.errorFields) return;
      message.error('Error al guardar');
    }
  };

  // ── Columnas historial ───────────────────────────────────────────────────────
  const columnsMovs = [
    {
      title: 'Fecha', dataIndex: 'fecha', key: 'fecha', width: 150,
      render: f => <span style={{ fontSize: 12 }}>{fmtFecha(f)}</span>,
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.fecha) - new Date(b.fecha),
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 110,
      filters: [
        { text: 'Entrada', value: 'entrada' },
        { text: 'Salida',  value: 'salida'  },
        { text: 'Ajuste',  value: 'ajuste'  },
      ],
      onFilter: (val, r) => r.tipo === val,
      render: t => {
        const cfg = MOV_CONFIG[t] || {};
        return <Tag icon={cfg.icon} color={cfg.color}>{cfg.label || t}</Tag>;
      },
    },
    {
      title: 'Cantidad', key: 'cant', width: 100,
      render: (_, r) => {
        const sign  = r.tipo === 'salida' ? '−' : r.tipo === 'entrada' ? '+' : '→';
        const color = r.tipo === 'salida' ? hssColors.danger : r.tipo === 'entrada' ? hssColors.success : hssColors.warning;
        return <span style={{ fontWeight: 700, fontSize: 15, color }}>{sign}{r.cantidad}</span>;
      },
    },
    {
      title: 'Saldo', key: 'saldo', width: 130,
      render: (_, r) => (
        <Space size={4}>
          <span style={{ color: hssColors.textSecondary, fontFamily: 'monospace' }}>{r.saldo_anterior}</span>
          <span style={{ color: hssColors.gray400 }}>→</span>
          <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{r.saldo_nuevo}</span>
        </Space>
      ),
    },
    {
      title: 'Motivo', dataIndex: 'motivo', key: 'motivo',
      render: m => m || <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Referencia', dataIndex: 'referencia', key: 'ref', width: 140,
      render: r => r ? <Tag style={{ fontFamily: 'monospace' }}>{r}</Tag> : <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Usuario', dataIndex: 'usuario_nombre', key: 'usuario', width: 130,
      render: u => <span style={{ color: hssColors.textSecondary, fontSize: 12 }}>{u || '—'}</span>,
    },
  ];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, padding: '24px 32px' }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (!item) return null;

  const stockColor = (item.cantidad || 0) === 0
    ? hssColors.danger
    : (item.stock_minimo != null && (item.cantidad || 0) <= item.stock_minimo)
      ? hssColors.warning
      : hssColors.success;

  const entradas = movs.filter(m => m.tipo === 'entrada').reduce((s, m) => s + m.cantidad, 0);
  const salidas  = movs.filter(m => m.tipo === 'salida').reduce((s, m) => s + m.cantidad, 0);

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <AppstoreOutlined style={{ fontSize: 32, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>
                {item.nombre}
              </h1>
              <Space size={8} style={{ marginTop: 2 }}>
                <Tag color={catColor(item.categoria)}>{catLabel(item.categoria)}</Tag>
                <span style={{ fontSize: 13, color: hssColors.textSecondary }}>{item.unidad}</span>
                {item.ubicacion && <span style={{ fontSize: 13, color: hssColors.textSecondary }}>· {item.ubicacion}</span>}
              </Space>
            </div>
          </div>
          <Space>
            <Button icon={<EditOutlined />} onClick={abrirEditar} style={{ borderRadius: 8 }}>
              Editar
            </Button>
            <Button
              type="primary"
              icon={<SwapOutlined />}
              onClick={abrirMovimiento}
              style={{ borderRadius: 8 }}
            >
              Registrar movimiento
            </Button>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/accesorios')} style={{ borderRadius: 8 }}>
              Volver
            </Button>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>
              Inicio
            </Button>
          </Space>
        </div>
      </div>

      {/* Contenido */}
      <div style={{ padding: '24px 32px', flex: 1 }}>
        <Tabs
          defaultActiveKey="info"
          size="large"
          style={{ background: hssColors.backgroundSecondary, borderRadius: 12, padding: '0 16px', boxShadow: commonStyles.shadowMd }}
          items={[
            {
              key: 'info',
              label: <Space><InfoCircleOutlined />Información</Space>,
              children: (
                <div style={{ padding: '16px 0' }}>
                  {/* Stats rápidos */}
                  <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    {[
                      { title: 'Stock actual', value: item.cantidad ?? 0, suffix: item.unidad, color: stockColor },
                      { title: 'Total entradas', value: entradas, color: hssColors.success },
                      { title: 'Total salidas',  value: salidas,  color: hssColors.danger  },
                      { title: 'Movimientos',    value: movs.length, color: hssColors.primary },
                    ].map(s => (
                      <Col xs={12} lg={6} key={s.title}>
                        <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: `4px solid ${s.color}` }}>
                          <Statistic
                            title={<span style={{ color: hssColors.textSecondary, fontSize: 13 }}>{s.title}</span>}
                            value={s.value}
                            suffix={s.suffix ? <span style={{ fontSize: 13, color: hssColors.textSecondary }}>{s.suffix}</span> : undefined}
                            valueStyle={{ color: s.color, fontWeight: 700 }}
                          />
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  {/* Datos del ítem */}
                  <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
                    <Row gutter={[32, 20]}>
                      {[
                        { label: 'Nombre',         value: item.nombre },
                        { label: 'Categoría',      value: <Tag color={catColor(item.categoria)}>{catLabel(item.categoria)}</Tag> },
                        { label: 'Unidad',         value: item.unidad },
                        { label: 'Precio unitario', value: item.precio_unitario || '—' },
                        { label: 'Ubicación',      value: item.ubicacion || '—' },
                        { label: 'Stock mínimo',   value: item.stock_minimo != null ? `${item.stock_minimo} ${item.unidad}` : '—' },
                        { label: 'Descripción',    value: item.descripcion || '—' },
                        { label: 'Última actualización', value: fmtFecha(item.fecha_actualizacion) },
                      ].map(f => (
                        <Col xs={24} sm={12} lg={8} key={f.label}>
                          <div style={{ fontSize: 12, color: hssColors.textSecondary, marginBottom: 4 }}>{f.label}</div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{f.value}</div>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                </div>
              ),
            },
            {
              key: 'movimientos',
              label: (
                <Space>
                  <HistoryOutlined />
                  Movimientos
                  <Badge count={movs.length} style={{ background: hssColors.primary }} />
                </Space>
              ),
              children: (
                <Card
                  style={{ borderRadius: 12, border: 'none', marginBottom: 8 }}
                  styles={{ body: { padding: 0 } }}
                  extra={
                    <Button
                      type="primary"
                      icon={<SwapOutlined />}
                      onClick={abrirMovimiento}
                      size="small"
                    >
                      Registrar movimiento
                    </Button>
                  }
                >
                  <Table
                    columns={columnsMovs}
                    dataSource={movs}
                    rowKey="id"
                    loading={movLoading}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={{
                      pageSize: 25,
                      showSizeChanger: true,
                      showTotal: t => `${t} movimientos`,
                    }}
                    locale={{ emptyText: 'Sin movimientos registrados' }}
                  />
                </Card>
              ),
            },
          ]}
        />
      </div>

      {/* Modal registrar movimiento */}
      <Modal
        open={movModalOpen}
        onCancel={() => setMovModalOpen(false)}
        onOk={guardarMovimiento}
        title={<Space><SwapOutlined style={{ color: hssColors.primary }} />Registrar movimiento — {item.nombre}</Space>}
        okText="Registrar"
        cancelText="Cancelar"
        width={440}
        destroyOnClose
      >
        <div style={{ marginBottom: 16, padding: '10px 14px', background: hssColors.gray50, borderRadius: 8, border: `1px solid ${hssColors.gray200}` }}>
          <span style={{ color: hssColors.textSecondary, fontSize: 13 }}>Stock actual: </span>
          <span style={{ fontWeight: 700, fontSize: 18, color: stockColor }}>{item.cantidad ?? 0}</span>
          <span style={{ color: hssColors.textSecondary, fontSize: 13 }}> {item.unidad}</span>
        </div>

        <Form form={movForm} layout="vertical">
          <Form.Item name="tipo" label="Tipo de movimiento" rules={[{ required: true }]}>
            <Select size="large">
              <Option value="entrada">
                <Space><ArrowUpOutlined style={{ color: hssColors.success }} /><strong>Entrada</strong> — agregar unidades</Space>
              </Option>
              <Option value="salida">
                <Space><ArrowDownOutlined style={{ color: hssColors.danger }} /><strong>Salida</strong> — retirar unidades</Space>
              </Option>
              <Option value="ajuste">
                <Space><AimOutlined style={{ color: hssColors.warning }} /><strong>Ajuste</strong> — establecer cantidad exacta</Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="cantidad"
            label={movTipo === 'ajuste' ? 'Nueva cantidad total' : movTipo === 'salida' ? 'Unidades a retirar' : 'Unidades a agregar'}
            rules={[{ required: true, message: 'Ingrese una cantidad' }]}
            help={movTipo === 'ajuste' ? 'El stock quedará en este valor exacto.' : undefined}
          >
            <InputNumber min={1} size="large" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="motivo" label="Motivo" rules={[{ required: true, message: 'Seleccione un motivo' }]}>
            <Select placeholder="Seleccionar motivo" showSearch>
              {(MOTIVOS[movTipo] || MOTIVOS.entrada).map(m => <Option key={m} value={m}>{m}</Option>)}
            </Select>
          </Form.Item>

          <Form.Item name="referencia" label="Referencia (opcional)">
            <Input placeholder="Ej: Orden #45, Factura 1234..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal editar metadatos */}
      <Modal
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={guardarEditar}
        title="Editar ítem"
        okText="Guardar"
        cancelText="Cancelar"
        width={440}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="nombre" label="Nombre" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="categoria" label="Categoría" rules={[{ required: true }]}>
                <Select>
                  {CATEGORIAS.map(c => <Option key={c.valor} value={c.valor}><Tag color={c.color}>{c.label}</Tag></Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="unidad" label="Unidad">
                <Select>
                  {['unidades', 'metros', 'juegos', 'piezas', 'rollos', 'litros', 'bolsas'].map(u =>
                    <Option key={u} value={u}>{u}</Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="precio_unitario" label="Precio unitario">
                <Input placeholder="Ej: 15.000 Gs" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ubicacion" label="Ubicación">
                <Input placeholder="Ej: Estante A3" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="stock_minimo" label="Stock mínimo" help="Genera alerta cuando el stock llega a este nivel.">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Ej: 5" />
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Footer />
    </div>
  );
};

export default AccesorioDetalle;
